import React, { useEffect, useState } from 'react';
import { Container, Typography, Grid, Card, CardContent, Divider, Box, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../services/api';
import { useUser } from '../contexts/UserContext';
import { getCashFlowSummary } from '../services/cashflowService';

import 'chart.js/auto';
import { Bar } from 'react-chartjs-2';

/**
 * Returns the last six months, newest on the left.
 * Each month is formatted as "YYYY-MM".
 */
const getLastSixMonths = () => {
  const now = new Date();
  const months = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}`);
  }
  return months;
};

/**
 * Converts "YYYY-MM" -> "MM-YYYY" for display.
 */
const formatMonthLabel = (ym) => {
  const [year, month] = ym.split('-');
  return `${month}-${year}`;
};

const DashboardPage = () => {
  // Client data
  const [data, setData] = useState({ totalDebt: 0, numberOfClients: 0 });
  const [paymentsData, setPaymentsData] = useState([]);
  
  // Supplier data
  const [supplierData, setSupplierData] = useState({
    totalDebtToSuppliers: 0,
    numberOfSuppliers: 0,
    activeSuppliers: 0
  });
  const [supplierPurchasesData, setSupplierPurchasesData] = useState([]);
  const [supplierPaymentsData, setSupplierPaymentsData] = useState([]);

  // Cash flow data
  const [cashFlowSummary, setCashFlowSummary] = useState({
    total_in: 0,
    total_out: 0,
    net: 0
  });

  const { userProfile } = useUser();
  const role = userProfile?.role;
  const navigate = useNavigate();

  // Track route changes so we can re-fetch data on each visit.
  const location = useLocation();

  const fetchDashboardData = async () => {
    try {
      // Fetch client metadata
      const response = await API.get('/metadata/');
      setData({
        totalDebt: response.data.total_debt,
        numberOfClients: response.data.number_of_clients,
      });
      setPaymentsData(response.data.payments_over_months || []);
      
      // Fetch supplier summary
      try {
        const supplierResponse = await API.get('/supplier/summary/');
        if (supplierResponse.data) {
          setSupplierData({
            totalDebtToSuppliers: supplierResponse.data.total_debt_to_suppliers || 0,
            numberOfSuppliers: supplierResponse.data.number_of_suppliers || 0,
            activeSuppliers: supplierResponse.data.active_suppliers || 0,
            debtByCurrency: supplierResponse.data.debt_by_currency || null,
            foreignCurrencies: supplierResponse.data.foreign_currencies || [],
          });
          setSupplierPurchasesData(supplierResponse.data.purchases_over_months || []);
          setSupplierPaymentsData(supplierResponse.data.payments_over_months || []);
        }
      } catch (supplierError) {
        console.error('Error fetching supplier data:', supplierError);
        // Keep default values if supplier endpoint fails
      }

      // Fetch cash flow summary
      try {
        const cashFlowResponse = await getCashFlowSummary({ period: 'month' });
        if (cashFlowResponse) {
          setCashFlowSummary({
            total_in: cashFlowResponse.total_in || 0,
            total_out: cashFlowResponse.total_out || 0,
            net: cashFlowResponse.net || 0,
            combined_net: cashFlowResponse.combined_net ?? cashFlowResponse.net ?? 0,
            foreign_out: cashFlowResponse.foreign_out || null,
          });
        }
      } catch (cashFlowError) {
        console.error('Error fetching cash flow data:', cashFlowError);
        // Keep default values if cash flow endpoint fails
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  // Re-fetch data whenever role or location changes.
  useEffect(() => {
    fetchDashboardData();
  }, [role, location]);

  // Build a six-month array ensuring each month has a value for client payments
  const lastSixMonths = getLastSixMonths();
  const fullMonths = lastSixMonths.map((ym) => {
    const found = paymentsData.find((item) => item.month === ym);
    return {
      month: ym,
      total: found ? found.total : 0,
    };
  });

  // Build a six-month array for supplier purchases
  const fullSupplierMonths = lastSixMonths.map((ym) => {
    const found = supplierPurchasesData.find((item) => item.month === ym);
    return found ? { ...found, month: ym } : { month: ym, total: 0 };
  });

  // Build a six-month array for supplier payments
  const fullSupplierPaymentMonths = lastSixMonths.map((ym) => {
    const found = supplierPaymentsData.find((item) => item.month === ym);
    return found ? { ...found, month: ym } : { month: ym, total: 0 };
  });

  // Chart dataset with green bars for client payments
  const barChartData = {
    labels: fullMonths.map((m) => formatMonthLabel(m.month)),
    datasets: [
      {
        label: 'תשלומים מלקוחות',
        data: fullMonths.map((m) => m.total),
        backgroundColor: 'green',
        barPercentage: 0.9,
        categoryPercentage: 0.8,
      },
    ],
  };

  // Foreign currency colors — distinct from ILS bars (light blue #4dabf7 for purchases, red #ff6b6b for payments)
  const foreignCurrencyColors = { USD: '#ff9800', EUR: '#7c4dff' };
  const foreignCurrencyLabels = { USD: '$ דולר', EUR: '€ אירו' };
  const activeForeignCurrencies = supplierData.foreignCurrencies || [];

  // Chart dataset with LIGHT BLUE bars for supplier purchases — stacked with foreign currencies
  const supplierBarChartData = {
    labels: fullSupplierMonths.map((m) => formatMonthLabel(m.month)),
    datasets: [
      {
        label: 'רכישות ₪ שקל',
        data: fullSupplierMonths.map((m) => m.total),
        backgroundColor: '#4dabf7',
        barPercentage: 0.9,
        categoryPercentage: 0.8,
        stack: 'purchases',
      },
      ...activeForeignCurrencies.map((curr) => ({
        label: `רכישות ${foreignCurrencyLabels[curr] || curr}`,
        data: fullSupplierMonths.map((m) => m[curr] || 0),
        backgroundColor: foreignCurrencyColors[curr] || '#757575',
        barPercentage: 0.9,
        categoryPercentage: 0.8,
        stack: `purchases_${curr}`,
      })),
    ],
  };

  // Chart dataset with RED/ORANGE bars for supplier payments — stacked with foreign currencies
  const supplierPaymentsChartData = {
    labels: fullSupplierPaymentMonths.map((m) => formatMonthLabel(m.month)),
    datasets: [
      {
        label: 'תשלומים ₪ שקל',
        data: fullSupplierPaymentMonths.map((m) => m.total),
        backgroundColor: '#ff6b6b',
        barPercentage: 0.9,
        categoryPercentage: 0.8,
        stack: 'payments',
      },
      ...activeForeignCurrencies.map((curr) => ({
        label: `תשלומים ${foreignCurrencyLabels[curr] || curr}`,
        data: fullSupplierPaymentMonths.map((m) => m[curr] || 0),
        backgroundColor: foreignCurrencyColors[curr] || '#757575',
        barPercentage: 0.9,
        categoryPercentage: 0.8,
        stack: `payments_${curr}`,
      })),
    ],
  };

  // Chart options for client payments
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 0 },
    elements: {
      bar: {
        borderSkipped: false,
      },
    },
    scales: {
      x: {
        type: 'category',
        offset: true,
        ticks: {
          align: 'center',
          font: { size: 14, weight: 'bold' },
        },
        title: {
          display: true,
          text: 'חודש',
          font: { size: 16, weight: 'bold' },
        },
      },
      y: {
        ticks: {
          font: { size: 14, weight: 'bold' },
          callback: (value) =>
            new Intl.NumberFormat('he-IL', {
              style: 'currency',
              currency: 'ILS',
            }).format(value),
        },
        title: {
          display: true,
          text: 'סכום תשלום',
          font: { size: 16, weight: 'bold' },
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: 'תשלומים מלקוחות לפי חודש',
        font: { size: 18, weight: 'bold' },
      },
      legend: {
        labels: {
          font: { size: 14, weight: 'bold' },
        },
      },
      tooltip: {
        titleFont: { size: 16, weight: 'bold' },
        bodyFont: { size: 14, weight: 'bold' },
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const amount = context.parsed.y;
            const formatted = new Intl.NumberFormat('he-IL', {
              style: 'currency',
              currency: 'ILS',
            }).format(amount);
            return `${label}: ${formatted}`;
          },
        },
      },
    },
  };

  // Tooltip callback that detects currency from dataset label
  const supplierTooltipCallback = (context) => {
    const label = context.dataset.label || '';
    const amount = context.parsed.y;
    // Detect currency from label
    let curr = 'ILS';
    if (label.includes('דולר') || label.includes('USD')) curr = 'USD';
    else if (label.includes('אירו') || label.includes('EUR')) curr = 'EUR';
    const formatted = new Intl.NumberFormat('he-IL', { style: 'currency', currency: curr }).format(amount);
    return `${label}: ${formatted}`;
  };

  // Chart options for supplier purchases
  const supplierBarChartOptions = {
    ...barChartOptions,
    scales: {
      ...barChartOptions.scales,
      y: {
        ...barChartOptions.scales.y,
        title: {
          display: true,
          text: 'סכום רכישות',
          font: { size: 16, weight: 'bold' },
        },
      },
    },
    plugins: {
      ...barChartOptions.plugins,
      title: {
        display: true,
        text: 'רכישות מספקים לפי חודש',
        font: { size: 18, weight: 'bold' },
      },
      tooltip: {
        ...barChartOptions.plugins.tooltip,
        callbacks: { label: supplierTooltipCallback },
      },
    },
  };

  // Chart options for supplier payments
  const supplierPaymentsChartOptions = {
    ...barChartOptions,
    scales: {
      ...barChartOptions.scales,
      y: {
        ...barChartOptions.scales.y,
        title: {
          display: true,
          text: 'סכום תשלומים',
          font: { size: 16, weight: 'bold' },
        },
      },
    },
    plugins: {
      ...barChartOptions.plugins,
      title: {
        display: true,
        text: 'תשלומים לספקים לפי חודש',
        font: { size: 18, weight: 'bold' },
      },
      tooltip: {
        ...barChartOptions.plugins.tooltip,
        callbacks: { label: supplierTooltipCallback },
      },
    },
  };

  return (
    <Container maxWidth="lg" sx={{ mx: { xs: 0, md: 'auto' }, mr: { md: 0 } }}>
      {/* Cash Flow Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontSize: { xs: '1.1rem', sm: '2rem' },
            fontWeight: 'bold',
            color: '#1976d2', // Blue color for cash flow section header
            mb: 3,
            borderBottom: '3px solid #1976d2',
            paddingBottom: 1,
            display: 'inline-block',
          }}
        >
          תזרים מזומנים - החודש
        </Typography>

        <Grid container spacing={{ xs: 1, sm: 3 }} sx={{ alignItems: 'stretch' }}>
          <Grid item xs={4}>
            <Card sx={{
              p: { xs: 0.5, sm: 2 },
              minHeight: { xs: 'auto', sm: '150px' },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '1.25rem', lg: '1.5rem' },
                    mb: 1
                  }}
                >
                  נכנס
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '2.5rem', lg: '3rem' },
                    color: 'success.main',
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(cashFlowSummary.total_in)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={4}>
            <Card sx={{
              p: { xs: 0.5, sm: 2 },
              minHeight: { xs: 'auto', sm: '150px' },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '1.25rem', lg: '1.5rem' },
                    mb: 1
                  }}
                >
                  יוצא
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '2.5rem', lg: '3rem' },
                    color: 'error.main',
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(cashFlowSummary.total_out)}
                </Typography>
                {cashFlowSummary.foreign_out && Object.entries(cashFlowSummary.foreign_out).map(([curr, data]) => {
                  const foreignAmt = typeof data === 'object' ? data.amount : data;
                  const ilsAmt = typeof data === 'object' ? data.ils : null;
                  return (
                    <Box key={curr} sx={{ mt: 0.5 }}>
                      <Typography variant="h4" sx={{
                        color: 'error.main',
                        fontSize: { xs: '1.1rem', sm: '2.5rem', lg: '3rem' },
                        wordBreak: 'break-word',
                        lineHeight: 1.2,
                      }}>
                        {new Intl.NumberFormat('he-IL', { style: 'currency', currency: curr, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(foreignAmt)}
                      </Typography>
                      {ilsAmt != null && (
                        <Typography variant="body2" color="text.secondary">
                          ≈ {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(ilsAmt)}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={4}>
            <Card sx={{
              p: { xs: 0.5, sm: 2 },
              minHeight: { xs: 'auto', sm: '150px' },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '1.25rem', lg: '1.5rem' },
                    mb: 1
                  }}
                >
                  נטו
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '2.5rem', lg: '3rem' },
                    color: (cashFlowSummary.combined_net ?? cashFlowSummary.net) >= 0 ? 'success.main' : 'error.main',
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(cashFlowSummary.combined_net ?? cashFlowSummary.net)}
                </Typography>
                {cashFlowSummary.foreign_out && Object.entries(cashFlowSummary.foreign_out).map(([curr, data]) => {
                  const foreignAmt = typeof data === 'object' ? data.amount : data;
                  return (
                    <Typography key={curr} variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      כולל {new Intl.NumberFormat('he-IL', { style: 'currency', currency: curr, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(foreignAmt)} הוצאות
                    </Typography>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>

          {/* Action Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/cashflow')}
                sx={{ fontSize: '1rem', px: 3 }}
              >
                צפה בתזרים מלא
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Clients Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontSize: { xs: '1.1rem', sm: '2rem' },
            fontWeight: 'bold',
            color: '#2e7d32', // Dark green for clients section header
            mb: 3,
            borderBottom: '3px solid #2e7d32',
            paddingBottom: 1,
            display: 'inline-block',
          }}
        >
          סקירת לקוחות
        </Typography>

        <Grid container spacing={{ xs: 1, sm: 3 }}>
          <Grid item xs={6} lg={4}>
            <Card sx={{
              p: { xs: 0.5, sm: 2 },
              minHeight: { xs: 'auto', sm: '150px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '1.25rem', lg: '1.5rem' },
                    mb: 1
                  }}
                >
                  לקוחות
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '2.5rem', lg: '3rem' },
                    wordBreak: 'break-word', // Allow long numbers to wrap
                    lineHeight: 1.2
                  }}
                >
                  {data.numberOfClients}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} lg={4}>
            <Card sx={{
              p: { xs: 0.5, sm: 2 },
              minHeight: { xs: 'auto', sm: '150px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '1.25rem', lg: '1.5rem' },
                    mb: 1
                  }}
                >
                  חובות הלקוחות
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '2.5rem', lg: '3rem' },
                    color: 'error.main',
                    wordBreak: 'break-word', // Allow long numbers to wrap
                    lineHeight: 1.2
                  }}
                >
                  {new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(data.totalDebt)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: { xs: 1, md: 2 }, overflow: 'visible' }}>
              <CardContent sx={{ p: { xs: 1, md: 2 } }}>
                <Box
                  sx={{
                    margin: '0 auto',
                    width: '100%',
                    maxWidth: '800px',
                    height: { xs: 250, sm: 350, md: 500 },
                  }}
                >
                  <Bar data={barChartData} options={barChartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Suppliers Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontSize: { xs: '1.1rem', sm: '2rem' },
            fontWeight: 'bold',
            color: '#d32f2f', // Red color for suppliers section header
            mb: 3,
            borderBottom: '3px solid #d32f2f',
            paddingBottom: 1,
            display: 'inline-block',
          }}
        >
          סקירת ספקים
        </Typography>

        <Grid container spacing={{ xs: 1, sm: 3 }}>
          <Grid item xs={6} lg={4}>
            <Card sx={{
              p: { xs: 0.5, sm: 2 },
              minHeight: { xs: 'auto', sm: '150px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '1.25rem', lg: '1.5rem' },
                    mb: 1
                  }}
                >
                  ספקים פעילים
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '2.5rem', lg: '3rem' },
                    wordBreak: 'break-word', // Allow long numbers to wrap
                    lineHeight: 1.2
                  }}
                >
                  {supplierData.activeSuppliers || supplierData.numberOfSuppliers}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} lg={4}>
            <Card sx={{
              p: { xs: 0.5, sm: 2 },
              minHeight: { xs: 'auto', sm: '150px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '1.25rem', lg: '1.5rem' },
                    mb: 1
                  }}
                >
                  חובות לספקים
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '2.5rem', lg: '3rem' },
                    color: 'error.main',
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(supplierData.totalDebtToSuppliers)}
                </Typography>
                {supplierData.debtByCurrency && Object.entries(supplierData.debtByCurrency).map(([curr, amount]) => {
                  const currLabels = { USD: '$ דולר', EUR: '€ אירו' };
                  return (
                    <Box key={curr} sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {currLabels[curr] || curr}
                      </Typography>
                      <Typography variant="h5" sx={{ color: 'error.main', fontSize: { xs: '1.4rem', sm: '1.8rem', lg: '2.2rem' } }}>
                        {new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: curr,
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(amount)}
                      </Typography>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>

          {/* Purchases Chart - Light Blue */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ p: { xs: 1, md: 2 }, overflow: 'visible' }}>
              <CardContent sx={{ p: { xs: 1, md: 2 } }}>
                <Box
                  sx={{
                    margin: '0 auto',
                    width: '100%',
                    height: { xs: 250, sm: 300, md: 400 },
                  }}
                >
                  <Bar data={supplierBarChartData} options={supplierBarChartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Payments Chart - Red/Orange */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ p: { xs: 1, md: 2 }, overflow: 'visible' }}>
              <CardContent sx={{ p: { xs: 1, md: 2 } }}>
                <Box
                  sx={{
                    margin: '0 auto',
                    width: '100%',
                    height: { xs: 250, sm: 300, md: 400 },
                  }}
                >
                  <Bar data={supplierPaymentsChartData} options={supplierPaymentsChartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default DashboardPage;